import type { CategorieDocument, StatutDocument, VersionDocument } from './types.js';

/**
 * Libellés et modèles de texte de l'écran Documents : des DONNÉES versionnées (jamais du code en dur dans les écrans).
 * Une évolution de vocabulaire change la version, pas la logique.
 */
export const LIBELLES_CATEGORIES: { version: string; valeurs: Readonly<Record<CategorieDocument, string>> } = {
  version: '2026-10-17',
  valeurs: {
    etat_civil: 'État civil',
    titre: 'Titre de propriété',
    mandat: 'Mandat',
    procuration: 'Procuration',
    retour_notaire: 'Retour du notaire',
    courrier: 'Courrier',
    facture: 'Facture',
    identite: 'Pièce d’identité',
    autre: 'Autre pièce',
  },
};

export const LIBELLES_STATUTS: { version: string; valeurs: Readonly<Record<StatutDocument, string>> } = {
  version: '2026-10-17',
  valeurs: {
    recu: 'Reçue',
    a_verifier: 'À vérifier',
    valide: 'Validée',
    refuse: 'Refusée',
    non_signe: 'Non signée',
  },
};

export const LIBELLES_SOURCES: { version: string; valeurs: Readonly<Record<VersionDocument['source'], string>> } = {
  version: '2026-10-17',
  valeurs: {
    whatsapp: 'WhatsApp',
    email: 'E-mail',
    portail: 'Dépôt sur le portail',
    docuseal: 'Signature électronique',
    espace_clerc: 'Espace du clerc',
    rendez_vous: 'Remis en rendez-vous',
  },
};

export const MODELE_MEILLEURE_PHOTO = {
  version: '2026-10-17',
  texte:
    'Bonjour {prenom},\n\nLa photo de votre {piece} n’est pas assez nette pour que nous puissions la lire. ' +
    'Pourriez-vous en prendre une nouvelle, bien à plat et bien éclairée, grâce à ce lien : {lien}\n\nMerci d’avance.\n\nCabinet MAAT',
  /** Variante quand le lien réel de la page d'envoi n'est pas connu : jamais d'emplacement ni de crochets dans le texte. */
  texteSansLien:
    'Bonjour {prenom},\n\nLa photo de votre {piece} n’est pas assez nette pour que nous puissions la lire. ' +
    'Pourriez-vous en prendre une nouvelle, bien à plat et bien éclairée ?\n\nMerci d’avance.\n\nCabinet MAAT',
} as const;

/** Motifs de refus d'une pièce : les codes sont ceux du serveur (`pieces-attendues.v1.json`), les libellés sont vus par l'équipe. */
export const MOTIFS_REFUS_PIECE = {
  version: '2026-10-26',
  valeurs: [
    { code: 'illisible', libelle: 'Pièce illisible' },
    { code: 'mauvaise_piece', libelle: 'Ce n’est pas la pièce demandée' },
    { code: 'perimee', libelle: 'Pièce périmée' },
    { code: 'autre', libelle: 'Autre motif' },
  ],
  codeAutre: 'autre',
} as const;

/** Avis du contrôle automatique de netteté (CA-BRI-06a, maquette D09). Seuil et phrases sont des données versionnées. */
export const AVIS_NETTETE = {
  version: '2026-10-17',
  /** Au-delà de ce score de flou (0 = net, 1 = très flou), l'avis passe de « nette » à « un peu floue ». */
  seuilFlou: 0.3,
  textes: {
    nette: 'Contrôle automatique : photo nette.',
    floueLisible: 'Contrôle automatique : photo un peu floue, mais le texte reste lisible.',
    illisible: 'Contrôle automatique : photo trop floue, le texte n’est pas lisible. Pensez à demander une meilleure photo.',
    inconnu: 'Contrôle automatique : pas encore effectué.',
  },
} as const;

/** Repli du message de meilleure photo quand l'écran ne sait pas à qui il s'adresse (à compléter par l'équipe avant l'envoi). */
export const REPLI_DEMANDE_PHOTO = {
  version: '2026-10-17',
  prenomHeritier: 'Madame, Monsieur',
  lien: '',
} as const;

/**
 * États des écrans branchés dans la coquille (D09 équipe, M05 héritier) : chargement, erreur, vide, invitation. Vouvoiement,
 * aucun terme technique, aucune somme, jamais le nom du mandant. Données versionnées.
 */
export const ETATS_ECRANS = {
  version: '2026-10-18',
  d09: {
    sansDossier: 'Ouvrez les documents depuis un dossier : ses pièces s’afficheront ici.',
    chargement: 'Chargement des documents en cours…',
    aucunePiece: 'Aucune pièce n’a encore été reçue pour ce dossier.',
    aucunResultat: 'Aucune pièce ne correspond à ces filtres.',
    ouverture: 'Ouverture de la pièce en cours…',
    decisionEnregistree: 'Décision enregistrée.',
    demandeEnregistree: 'Votre demande est enregistrée, mais l’envoi à l’héritier est indisponible : aucun message n’est parti. Prévenez-le directement.',
    motifRequis: 'Indiquez le motif du refus avant de le confirmer.',
    reessayer: 'Réessayer',
  },
  m05: {
    sansPiece: 'Ouvrez cette page depuis le bouton « Envoyer une photo » de votre espace : la pièce demandée s’affichera ici.',
    chargement: 'Chargement de votre demande en cours…',
    horsLigne:
      'Le réseau est coupé. Vous pouvez quand même prendre la photo : elle partira dès que le réseau revient, ou à votre prochaine ouverture de la page.',
    pieceGenerique: 'La pièce demandée par le Cabinet MAAT.',
    reessayer: 'Réessayer',
  },
} as const;

/**
 * Messages de la photo de pièce (M05), vus par l'héritier : vouvoiement, aucun terme technique. Données versionnées.
 * SYNTHESE §3, correction 9 : sur iPhone, aucun envoi en arrière-plan ; la photo repart au retour du réseau (événement
 * `online`, page ouverte) ou à la prochaine ouverture de la page. Aucun texte ne promet donc un envoi « automatique ».
 */
export const TEXTES_PHOTO = {
  version: '2026-10-18',
  textes: {
    termine: 'Merci, votre pièce a bien été envoyée. Cabinet MAAT',
    attenteReseau: 'Le réseau est coupé. Votre photo partira dès que le réseau revient, ou à votre prochaine ouverture de la page.',
    tropLourde:
      'Cette photo reste trop lourde pour être envoyée. Reprenez-la un peu plus de loin, avec moins de détails autour du document.',
    illisible: 'Cette photo n’a pas pu être lue. Reprenez-la.',
    corrompu: 'Votre photo n’a pas pu être vérifiée à l’arrivée. Reprenez-la, puis envoyez-la de nouveau.',
    inattendu: 'Une erreur est survenue de notre côté. Reprenez la photo ; si cela se reproduit, contactez le Cabinet MAAT.',
  },
} as const;
